import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Activity, Users, UserCheck, Eye, MousePointerClick, Globe,
  Smartphone, Monitor, ShieldAlert, TrendingUp, BarChart3,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar,
} from "recharts";
import RevenuePanel from "../components/RevenuePanel";
import AdminPromotions from "./AdminPromotions";

function Stat({ icon: Icon, label, value, sub, accent = "var(--bull)", testid }) {
  return (
    <div className="card-surface p-4 relative overflow-hidden" data-testid={testid}>
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{ background: `radial-gradient(circle at 100% 0%, ${accent}1f, transparent 60%)` }}
      />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
          <div className="label-mono">{label}</div>
        </div>
        <div
          className="font-mono text-2xl text-white font-semibold tabular-nums mt-1.5 tracking-tight"
          style={{ textShadow: `0 0 14px ${accent}44` }}
        >
          {typeof value === "number" ? value.toLocaleString() : value}
        </div>
        {sub && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

const COLORS = {
  visitors: "#00ff9c",
  signups: "#7aa2ff",
  scans: "#ffd166",
  grid: "rgba(255,255,255,0.06)",
  axis: "rgba(255,255,255,0.35)",
};

function GrowthChart({ data }) {
  return (
    <div className="card-surface p-4 sm:p-5" data-testid="growth-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[var(--bull)]" />
          <div className="text-white text-[15px] tracking-tight font-semibold">Visitors &amp; signups</div>
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm" style={{ background: COLORS.visitors }} /> Visitors
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm" style={{ background: COLORS.signups }} /> Signups
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm" style={{ background: COLORS.scans }} /> Scans
          </span>
        </div>
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id="gV" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.visitors} stopOpacity={0.45} />
                <stop offset="100%" stopColor={COLORS.visitors} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="gS" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.signups} stopOpacity={0.45} />
                <stop offset="100%" stopColor={COLORS.signups} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.scans} stopOpacity={0.40} />
                <stop offset="100%" stopColor={COLORS.scans} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="day" tick={{ fill: COLORS.axis, fontSize: 10 }} tickFormatter={(d) => d.slice(5)} stroke={COLORS.grid} />
            <YAxis tick={{ fill: COLORS.axis, fontSize: 10 }} stroke={COLORS.grid} />
            <Tooltip
              contentStyle={{
                background: "rgba(10,10,12,0.95)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10,
                fontSize: 12,
              }}
              labelStyle={{ color: "#fff" }}
            />
            <Area type="monotone" dataKey="visitors" stroke={COLORS.visitors} strokeWidth={1.8} fill="url(#gV)" />
            <Area type="monotone" dataKey="signups" stroke={COLORS.signups} strokeWidth={1.8} fill="url(#gS)" />
            <Area type="monotone" dataKey="scans" stroke={COLORS.scans} strokeWidth={1.8} fill="url(#gC)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ListPanel({ title, icon: Icon, items, keyName, valueName = "count", emptyText, testid }) {
  const top = (items || []).slice(0, 10);
  return (
    <div className="card-surface p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-white/70" />
        <div className="text-white text-[14px] font-semibold tracking-tight">{title}</div>
      </div>
      {top.length === 0 ? (
        <div className="text-white/40 text-xs">{emptyText || "No data yet."}</div>
      ) : (
        <ul className="space-y-1.5">
          {top.map((it, i) => (
            <li key={`${it[keyName] || 'row'}-${i}`} className="flex items-center justify-between text-[13px]">
              <span className="text-white/85 truncate">{it[keyName] || "—"}</span>
              <span className="font-mono text-white/90 tabular-nums">{(it[valueName] || 0).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RetentionPanel({ retention }) {
  if (!retention) return null;
  const rows = [
    { label: "1-day return", value: retention.d1, cohort: retention.d1_cohort },
    { label: "7-day return", value: retention.d7, cohort: retention.d7_cohort },
    { label: "30-day return", value: retention.d30, cohort: retention.d30_cohort },
  ];
  return (
    <div className="card-surface p-4 sm:p-5" data-testid="retention-panel">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-3.5 h-3.5 text-[var(--bull)]" />
        <div className="text-white text-[14px] font-semibold tracking-tight">User retention</div>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-white/80">{r.label}</span>
              <span className="font-mono text-white tabular-nums">{r.value}%</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${Math.min(100, r.value)}%`,
                  background: `linear-gradient(90deg, var(--bull), #7aa2ff)`,
                }}
              />
            </div>
            <div className="text-[10px] text-white/40 font-mono mt-0.5">
              cohort of {r.cohort} signups eligible
            </div>
          </div>
        ))}
        <div className="text-[11px] text-white/40 mt-2">
          Total signups: <span className="text-white/80 font-mono">{retention.total_signups}</span>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [window_, setWindow_] = useState(30);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [devices, setDevices] = useState([]);
  const [sources, setSources] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (user && !isAdmin) navigate("/dashboard", { replace: true });
  }, [user, isAdmin, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [o, ts, tt, dv, ct, src, ret] = await Promise.all([
        api.get("/api/admin/analytics/overview"),
        api.get(`/api/admin/analytics/timeseries?days=${window_}`),
        api.get(`/api/admin/analytics/top-tickers?days=${window_}&limit=10`),
        api.get("/api/admin/analytics/devices"),
        api.get("/api/admin/analytics/countries"),
        api.get("/api/admin/analytics/sources"),
        api.get("/api/admin/analytics/retention"),
      ]);
      setOverview(o.data);
      setSeries(ts.data.series || []);
      setTickers(tt.data.items || []);
      setDevices(dv.data.items || []);
      setCountries(ct.data.items || []);
      setSources(src.data.items || []);
      setRetention(ret.data);
    } catch (err) {
      // 403 → navigated out above; other errors are non-critical for admin
      console.warn("[Admin] analytics fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, window_]);

  const deviceData = useMemo(
    () => (devices || []).map((d) => ({ name: (d.device || "unknown").toUpperCase(), count: d.count })),
    [devices]
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card-surface p-8 text-center" data-testid="admin-forbidden">
          <ShieldAlert className="w-7 h-7 text-[var(--bear)] mx-auto mb-3" />
          <div className="text-white font-semibold">Admin access required</div>
          <div className="text-white/55 text-sm mt-1">Sign in as an admin to view analytics.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-page">
      <Header />
      <main id="main-content" className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
          <div>
            <div className="label-mono">FlowFinder · operator console</div>
            <h1 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight leading-tight mt-1">
              Analytics
            </h1>
            <div className="text-white/45 text-sm mt-1">
              Live for the last {window_} days. Bot traffic excluded from all counts.
            </div>
          </div>
          <div className="inline-flex items-center gap-2 self-start sm:self-auto">
            <a
              href="/admin/launch"
              data-testid="admin-launch-control-link"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-black bg-[#00ff9c] hover:bg-[#00e089] px-3.5 py-1.5 rounded-full transition"
            >
              🚀 Launch Control
            </a>
            <div className="inline-flex items-center bg-white/[0.04] border border-white/10 rounded-full p-1">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setWindow_(d)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-mono tracking-widest transition-all ${
                    window_ === d ? "bg-white text-black" : "text-white/65 hover:text-white"
                  }`}
                  data-testid={`admin-window-${d}d`}
                >
                  {d}D
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <Stat icon={Eye} label="Visitors today" value={overview?.visitors_today ?? "—"} testid="kpi-visitors-today" />
          <Stat icon={Activity} label="Active now" value={overview?.active_now ?? "—"} sub={`last ${overview?.active_window_minutes || 60}m`} accent="#7aa2ff" testid="kpi-active-now" />
          <Stat icon={UserCheck} label="Signups today" value={overview?.signups_today ?? "—"} accent="#ffd166" testid="kpi-signups-today" />
          <Stat icon={Users} label="Registered" value={overview?.total_users ?? "—"} sub="all-time" accent="#7aa2ff" testid="kpi-total-users" />
          <Stat icon={TrendingUp} label="Returning" value={overview?.returning_users ?? "—"} sub="multi-day active" testid="kpi-returning" />
          <Stat icon={MousePointerClick} label="Conversion" value={overview ? `${overview.conversion_rate}%` : "—"} sub="signup / visitor" accent="#ffd166" testid="kpi-conversion" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <Stat icon={Eye} label="Total visitors" value={overview?.total_visitors ?? "—"} sub="pageviews" testid="kpi-total-visitors" />
          <Stat icon={Users} label="Unique visitors" value={overview?.unique_visitors ?? "—"} testid="kpi-unique" />
          <Stat icon={Activity} label="DAU" value={overview?.dau ?? "—"} sub="last 24h" testid="kpi-dau" />
          <Stat icon={Activity} label="WAU" value={overview?.wau ?? "—"} sub="last 7d" accent="#7aa2ff" testid="kpi-wau" />
          <Stat icon={Activity} label="MAU" value={overview?.mau ?? "—"} sub="last 30d" accent="#ffd166" testid="kpi-mau" />
          <Stat icon={BarChart3} label="Total scans" value={overview?.total_scans ?? "—"} sub={`${overview?.scans_per_user ?? 0} per user`} testid="kpi-scans" />
        </div>

        {/* Growth chart */}
        <div className="mb-5">
          <GrowthChart data={series} />
        </div>

        {/* Revenue + profit */}
        <div className="mb-5">
          <RevenuePanel />
        </div>

        {/* Seasonal Promotions admin */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="label-mono">Seasonal Promotions</div>
          </div>
          <AdminPromotions />
        </div>

        {/* Bottom row: tickers / countries / devices / sources / retention */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <ListPanel
            title="Most searched tickers"
            icon={BarChart3}
            items={tickers}
            keyName="ticker"
            valueName="count"
            emptyText="No ticker searches yet."
            testid="top-tickers-panel"
          />
          <ListPanel
            title="Top countries"
            icon={Globe}
            items={countries}
            keyName="country"
            valueName="count"
            emptyText="Geo lookups still resolving…"
            testid="top-countries-panel"
          />
          <div className="card-surface p-4 sm:p-5" data-testid="devices-panel">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-3.5 h-3.5 text-white/70" />
              <div className="text-white text-[14px] font-semibold tracking-tight">Device mix</div>
            </div>
            {deviceData.length === 0 ? (
              <div className="text-white/40 text-xs">No data yet.</div>
            ) : (
              <div className="h-[160px]">
                <ResponsiveContainer>
                  <BarChart data={deviceData} margin={{ top: 8, right: 0, left: -22, bottom: 0 }}>
                    <CartesianGrid stroke={COLORS.grid} vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: COLORS.axis, fontSize: 10 }} stroke={COLORS.grid} />
                    <YAxis tick={{ fill: COLORS.axis, fontSize: 10 }} stroke={COLORS.grid} />
                    <Tooltip
                      contentStyle={{
                        background: "rgba(10,10,12,0.95)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="count" fill={COLORS.visitors} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="flex items-center gap-3 text-[11px] text-white/50 mt-2">
              <span className="inline-flex items-center gap-1"><Monitor className="w-3 h-3" /> desktop</span>
              <span className="inline-flex items-center gap-1"><Smartphone className="w-3 h-3" /> mobile</span>
              <span className="ml-auto font-mono">
                bots: {overview ? `${overview.bot_share}%` : "—"}
              </span>
            </div>
          </div>
          <ListPanel
            title="Top traffic sources"
            icon={Globe}
            items={sources}
            keyName="source"
            valueName="count"
            emptyText="No referrers yet."
            testid="top-sources-panel"
          />
          <RetentionPanel retention={retention} />
          <div className="card-surface p-4 sm:p-5" data-testid="meta-panel">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-3.5 h-3.5 text-white/70" />
              <div className="text-white text-[14px] font-semibold tracking-tight">Data quality</div>
            </div>
            <div className="space-y-2 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-white/70">Avg session</span>
                <span className="font-mono text-white">{overview ? `${overview.avg_session_min} min` : "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70">Tickers searched</span>
                <span className="font-mono text-white">{overview?.tickers_searched ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70">Bot share</span>
                <span className="font-mono text-white">{overview ? `${overview.bot_share}%` : "—"}</span>
              </div>
              <div className="text-[11px] text-white/45 leading-relaxed mt-2">
                Unique counts use a long-lived anon cookie so refreshes do not inflate metrics. Bots are filtered by User-Agent regex.
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-[11px] text-white/45 font-mono mt-4">refreshing…</div>
        )}
      </main>
      <Footer />
    </div>
  );
}
